import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createEmpresa,
  getEmpresa,
  listUsuariosDeEmpresa,
  updateEmpresa,
  updateUsuariosDeEmpresa,
  type CreateEmpresaRequest,
  type EmpresaUsuario,
  type UpdateEmpresaRequest,
} from "@/api/empresas";
import { listUsuarios } from "@/api/usuarios";
import { Button } from "@/components/ui/Button";
import { showToast } from "@/components/ui/Toast";
import { FormPageShell } from "@/components/ui/FormPageShell";
import { FormPageSkeleton } from "@/components/ui/FormPageSkeleton";
import { TabsNav, type TabsNavItem } from "@/components/ui/TabsNav";
import {
  EmpresaTabsContent,
  type EmpresaTabId,
  type EmpresaTabsState,
} from "@/components/features/empresas/EmpresaTabsContent";

const TABS: { label: string; items: TabsNavItem[] }[] = [
  {
    label: "Datos",
    items: [
      { id: "info", label: "Información" },
      { id: "contacto", label: "Contacto" },
      { id: "notas", label: "Notas" },
      { id: "scanner", label: "Config. Scanner" },
    ],
  },
  {
    label: "Configuración",
    items: [{ id: "miembros", label: "Miembros" }],
  },
  {
    label: "PUC",
    items: [
      { id: "puc-compras", label: "PUC Compras" },
      { id: "puc-ventas", label: "PUC Ventas" },
      { id: "puc-exterior", label: "PUC Exterior" },
      { id: "puc-legalizacion", label: "PUC Legalización" },
    ],
  },
];

export const EmpresaFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const empresaId = isEdit ? Number(id) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<EmpresaTabId>("info");
  const [razonSocial, setRazonSocial] = useState("");
  const [nit, setNit] = useState("");
  const [activa, setActiva] = useState(true);
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [persona, setPersona] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [modeloIa, setModeloIa] = useState("gemini-2.5-flash");
  const [temperature, setTemperature] = useState("0.10");
  const [umbral, setUmbral] = useState("0.70");
  const [heuristica, setHeuristica] = useState(true);

  const [selectedUsers, setSelectedUsers] = useState<
    Map<number, EmpresaUsuario["rol_empresa"]>
  >(new Map());
  const [pendingUserId, setPendingUserId] = useState<string>("");

  const { data: empresa, isLoading: loadingEmpresa } = useQuery({
    queryKey: ["empresa", empresaId],
    queryFn: () => getEmpresa(empresaId!),
    enabled: empresaId !== null,
  });

  const { data: allUsuarios = [] } = useQuery({
    queryKey: ["usuarios-all"],
    queryFn: () => listUsuarios(),
    select: (res) => res.data,
  });

  const { data: miembrosActuales = [] } = useQuery({
    queryKey: ["empresa-miembros", empresaId],
    queryFn: () => listUsuariosDeEmpresa(empresaId!),
    enabled: empresaId !== null,
  });

  useEffect(() => {
    if (empresa) {
      setRazonSocial(empresa.razon_social);
      setNit(empresa.nit);
      setActiva(Boolean(empresa.activa));
      setDireccion(empresa.direccion ?? "");
      setTelefono(empresa.telefono ?? "");
      setEmail(empresa.email_contacto ?? "");
      setPersona(empresa.persona_contacto ?? "");
      setObservaciones(empresa.observaciones ?? "");
      setModeloIa(empresa.config_scanner?.modelo_ia ?? "gemini-2.5-flash");
      setTemperature(
        String(empresa.config_scanner?.temperature ?? 0.1),
      );
      setUmbral(String(empresa.config_scanner?.umbral_confianza ?? 0.7));
      setHeuristica(
        empresa.config_scanner?.activar_heuristica ?? true,
      );
    }
  }, [empresa]);

  useEffect(() => {
    if (miembrosActuales.length > 0) {
      setSelectedUsers(
        new Map(miembrosActuales.map((m) => [m.usuario_id, m.rol_empresa])),
      );
    }
  }, [miembrosActuales]);

  const create = useMutation({
    mutationFn: (body: CreateEmpresaRequest) => createEmpresa(body),
    onSuccess: async (res) => {
      const newId = res.id;
      if (newId && selectedUsers.size > 0) {
        const usuarios: EmpresaUsuario[] = Array.from(
          selectedUsers.entries(),
        ).map(([usuario_id, rol_empresa]) => ({
          usuario_id,
          rol_empresa,
        }));
        try {
          await updateUsuariosDeEmpresa(newId, usuarios);
        } catch (err) {
          showToast(
            "warning",
            "Empresa creada, pero los miembros no se guardaron",
            (err as Error).message,
          );
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["empresas"] });
      showToast("success", "Empresa creada");
      navigate("/empresas");
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo crear", err.message),
  });

  const update = useMutation({
    mutationFn: (body: UpdateEmpresaRequest) =>
      updateEmpresa(empresaId!, body),
    onSuccess: async () => {
      if (empresaId) {
        const usuarios: EmpresaUsuario[] = Array.from(
          selectedUsers.entries(),
        ).map(([usuario_id, rol_empresa]) => ({
          usuario_id,
          rol_empresa: rol_empresa as EmpresaUsuario["rol_empresa"],
        }));
        try {
          await updateUsuariosDeEmpresa(empresaId, usuarios);
        } catch (err) {
          showToast(
            "warning",
            "Empresa guardada, pero los miembros no se actualizaron",
            (err as Error).message,
          );
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["empresas"] });
      await queryClient.invalidateQueries({ queryKey: ["empresa", empresaId] });
      showToast("success", "Empresa actualizada");
      navigate("/empresas");
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo actualizar", err.message),
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isEdit) {
      const body: UpdateEmpresaRequest = {
        razon_social: razonSocial,
        nit,
        activa,
        direccion,
        telefono,
        email_contacto: email,
        persona_contacto: persona,
        observaciones,
        config_scanner: {
          modelo_ia: modeloIa,
          temperature: Number(temperature) || 0.1,
          umbral_confianza: Number(umbral) || 0.7,
          activar_heuristica: heuristica,
        },
      };
      update.mutate(body);
    } else {
      const body: CreateEmpresaRequest = {
        razon_social: razonSocial,
        nit,
        activa,
        direccion,
        telefono,
        email_contacto: email,
        persona_contacto: persona,
        observaciones,
        config_scanner: {
          modelo_ia: modeloIa,
          temperature: Number(temperature) || 0.1,
          umbral_confianza: Number(umbral) || 0.7,
          activar_heuristica: heuristica,
        },
      };
      create.mutate(body);
    }
  };

  const addMember = () => {
    const userId = Number(pendingUserId);
    if (!userId || selectedUsers.has(userId)) {
      setPendingUserId("");
      return;
    }
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      next.set(userId, "lector");
      return next;
    });
    setPendingUserId("");
  };

  const removeMember = (userId: number) => {
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      next.delete(userId);
      return next;
    });
  };

  const changeRole = (userId: number, rol: EmpresaUsuario["rol_empresa"]) => {
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      next.set(userId, rol);
      return next;
    });
  };

  const tabsState: EmpresaTabsState = {
    razonSocial,
    setRazonSocial,
    nit,
    setNit,
    activa,
    setActiva,
    direccion,
    setDireccion,
    telefono,
    setTelefono,
    email,
    setEmail,
    persona,
    setPersona,
    observaciones,
    setObservaciones,
    modeloIa,
    setModeloIa,
    temperature,
    setTemperature,
    umbral,
    setUmbral,
    heuristica,
    setHeuristica,
    selectedUsers,
    setSelectedUsers,
    pendingUserId,
    setPendingUserId,
    allUsuarios,
    addMember,
    removeMember,
    setMemberRole: changeRole,
    isEdit,
    empresaId,
  };

  if (isEdit && loadingEmpresa) {
    return <FormPageSkeleton />;
  }

  return (
    <div className="space-y-4">
      <FormPageShell
        title={
          isEdit ? `Editar: ${empresa?.razon_social ?? ""}` : "Nueva empresa"
        }
        headerActions={
          <>
            <Button variant="secondary" onClick={() => navigate("/empresas")}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                (
                  document.getElementById("empresa-form") as HTMLFormElement
                )?.requestSubmit()
              }
              loading={create.isPending || update.isPending}
            >
              Guardar
            </Button>
          </>
        }
      >
        <div className="grid gap-4 lg:grid-cols-[200px_minmax(0,1fr)]">
          <TabsNav
            groups={TABS}
            active={activeTab}
            onChange={(id) => setActiveTab(id as EmpresaTabId)}
          />

          <form
            id="empresa-form"
            onSubmit={onSubmit}
            className="rounded-lg bg-white p-6 shadow-card"
          >
            <EmpresaTabsContent activeTab={activeTab} state={tabsState} />
          </form>
        </div>
      </FormPageShell>
    </div>
  );
};